import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, X, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImageUploadProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    className?: string;
}

export function ImageUpload({ value, onChange, label = "Imagem", className }: ImageUploadProps) {
    const [inputType, setInputType] = useState<'file' | 'url'>('url');
    const [preview, setPreview] = useState(value);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setPreview(value);
    }, [value]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit check (optional)
                alert("A imagem é muito grande. Tente uma menor que 5MB.");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                setPreview(result);
                onChange(result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const url = e.target.value;
        setPreview(url);
        onChange(url);
    };

    const clearImage = () => {
        setPreview('');
        onChange('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className={cn("space-y-3", className)}>
            <div className="flex items-center justify-between">
                <Label>{label}</Label>
                <div className="flex bg-muted rounded-lg p-1">
                    <button
                        type="button"
                        onClick={() => setInputType('url')}
                        className={cn(
                            "px-2 py-1 text-xs rounded-md transition-all flex items-center gap-1",
                            inputType === 'url' ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        <LinkIcon className="w-3 h-3" /> URL
                    </button>
                    <button
                        type="button"
                        onClick={() => setInputType('file')}
                        className={cn(
                            "px-2 py-1 text-xs rounded-md transition-all flex items-center gap-1",
                            inputType === 'file' ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        <Upload className="w-3 h-3" /> Upload
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-4">
                {preview ? (
                    <div className="relative group rounded-lg overflow-hidden border border-border bg-muted/30 aspect-video w-full max-h-[300px]">
                        <img
                            src={preview}
                            alt="Preview"
                            className="w-full h-full object-contain"
                        />
                        <button
                            type="button"
                            onClick={clearImage}
                            className="absolute top-2 right-2 bg-destructive/90 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center border-2 border-dashed border-muted-foreground/25 rounded-lg p-10 bg-muted/10 transition-colors hover:bg-muted/20">
                        <ImageIcon className="w-10 h-10 text-muted-foreground/50 mb-3" />
                        <p className="text-sm text-muted-foreground text-center">
                            Nenhuma imagem selecionada
                        </p>
                    </div>
                )}

                {inputType === 'url' ? (
                    <div className="flex gap-2">
                        <Input
                            type="text"
                            placeholder="Cole o link da imagem (https://...)"
                            value={value.startsWith('data:') ? '' : value}
                            onChange={handleUrlChange}
                            className="font-mono text-xs"
                        />
                    </div>
                ) : (
                    <div className="flex gap-2">
                        <div className="relative w-full">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="hidden"
                                id={`file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`}
                            />
                            <Label
                                htmlFor={`file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`}
                                className="flex items-center justify-center w-full px-4 py-2 border border-input bg-transparent rounded-md cursor-pointer hover:bg-accent hover:text-accent-foreground"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Escolher Arquivo
                            </Label>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
